import { useCallback, useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setPopupMessageModel } from '../../../redux/app/reducer'
import { apiParams } from '../../../utils'
import { userWiseRole } from '../../../utils/constant'
import { dayJs, SAVE_FORMAT } from '../../../utils/dayjs'
import debounce from '../../../utils/debounce'
import { isEqual, length } from '../../../utils/javascript'
import fiscalYearSelect from '../../common/container/fiscalYearSelect.container'
import { getUserList } from '../../userManagement/user.api'
import { exportToExcelApi, getSalesReportListApi } from '../report.api'

const salesReport = ({ activeTab }) => {
  const { t } = useTranslations()
  const { selector, dispatch } = useRedux()
  const { dealer } = userWiseRole
  const fiscalYear = selector(state => state.app.fiscalYear)
  const [selectedFilters, setSelectedFilters] = useState({
    dealer: '',
    location: [],
    channel: [],
  })
  // const [searchDealer, setSearchDealer] = useState('')
  const [dealerList, setDealerList] = useState({
    list: [{ label: 'user_AllDealers', value: 'ALL' }],
  })

  const [loading, setLoading] = useState(false)
  const [receiptVehicleDate, setReceiptVehicleDate] = useState({
    from: fiscalYear?.dateRange?.from,
    to: fiscalYear?.dateRange?.to,
  })
  const [processDate, setProcessDate] = useState({
    from: '',
    to: '',
  })
  const [salesReportList, setSalesReportList] = useState({})
  const { dateRangeProps, fiscalYearSelector } = fiscalYearSelect()

  useEffect(() => {
    getDealerListAPI()
  }, [])

  useEffect(() => {
    if (
      selectedFilters &&
      fiscalYear &&
      receiptVehicleDate &&
      processDate &&
      isEqual(activeTab, 'salesReport')
    ) {
      getStockReportList()
    }
  }, [selectedFilters, fiscalYear, receiptVehicleDate, processDate, activeTab])

  useEffect(() => {
    if (fiscalYear) {
      const startDate = dayJs(`${fiscalYear?.value}-04-01`).format(SAVE_FORMAT)
      const endDate = dayJs(`${fiscalYear?.value + 1}-03-31`).format(
        SAVE_FORMAT,
      )
      setReceiptVehicleDate({
        from: startDate,
        to: endDate,
      })
    }
  }, [fiscalYear])

  const onSelectDealerChange = values => {
    setSelectedFilters(prev => ({
      ...prev,
      dealer: values,
      location: [],
      channel: [],
    }))
    setDealerList(prev => ({
      ...prev,
      selectedDealer: prev.allDealerData?.find(item =>
        isEqual(item?.id, values),
      ),
      selectedLocations: [],
    }))
  }

  const onSelectLocationChange = values => {
    const selectedLocations =
      dealerList?.selectedDealer?.userLocationChannelDtos?.filter(item =>
        values?.includes(item?.locationId),
      ) || []
    setSelectedFilters(prev => ({
      ...prev,
      location: values,
      channel: prev.channel?.filter(channelId =>
        selectedLocations?.some(location =>
          location?.channels?.some(({ channelId: id }) =>
            isEqual(id, channelId),
          ),
        ),
      ),
    }))
    setDealerList(prev => ({
      ...prev,
      selectedLocations,
    }))
  }

  const onSelectChannelChange = values => {
    setSelectedFilters(prev => ({
      ...prev,
      channel: values,
    }))
  }

  // const onDealerSearch = async ({ values, pageNo = 1 }) => {
  //   setSearchDealer(values)
  //   debounce(() => searchDealerAPI({ values, pageNo }), 300)()
  // }

  const getDealerListAPI = async ({ pageNo = 1 } = {}) => {
    setLoading(true)
    const params = {
      roleId: dealer,
      relationType: 'ASSOCIATE',
    }
    const response = await getUserList({
      params: apiParams({ params, pageNo }),
    })

    if (length(response?.data?.list)) {
      const optionValue = response?.data?.list?.map(({ businessName, id }) => ({
        label: t(businessName),
        value: id,
      }))
      const userList = length(dealerList?.list)
        ? [...dealerList?.list, ...optionValue]
        : [...optionValue]

      const allDealerData = length(dealerList?.allDealerData)
        ? [...dealerList?.allDealerData, ...response?.data?.list]
        : [...response?.data?.list]

      if (isEqual(pageNo, 1)) {
        setSelectedFilters(prev => ({
          ...prev,
          dealer: userList[1]?.value,
        }))
        setDealerList(prev => ({
          ...prev,
          selectedDealer: allDealerData[0] || {},
        }))
        await getStockReportList()
      }
      setDealerList(prev => ({
        ...prev,
        ...response?.data,
        list: userList,
        allDealerData,
      }))
    }
    setLoading(false)
    return response?.data
  }

  // const searchDealerAPI = async ({ values = searchDealer, pageNo = 1 }) => {
  //   setLoading(true)
  //   if (values) {
  //     const params = {
  //       roleId: dealer,
  //       relationType: 'ASSOCIATE',
  //       businessName: values,
  //     }
  //     try {
  //       const result = await searchUserApi({
  //         params: apiParams({ params, pageNo }),
  //       })
  //       if (length(result?.data?.list)) {
  //         const optionValue = result?.data?.list?.map(
  //           ({ businessName, id }) => ({
  //             label: t(businessName),
  //             value: id,
  //           }),
  //         )
  //         const userList = length(dealerList?.searchDealerList)
  //           ? pageNo === 1
  //             ? [...optionValue]
  //             : [...dealerList?.searchDealerList, ...optionValue]
  //           : [...optionValue]

  //         setDealerList(prev => ({
  //           ...prev,
  //           searchDealerList: userList,
  //         }))
  //       } else {
  //         setDealerList(prev => ({
  //           ...prev,
  //           searchDealerList: [],
  //         }))
  //       }
  //     } catch (error) {
  //     } finally {
  //       setLoading(false)
  //     }
  //   }
  // }

  const getStockReportList = useCallback(
    async ({ pageNo = 1 } = {}) => {
      const params = {
        ...(selectedFilters?.dealer === 'ALL'
          ? { allDealers: true }
          : { dealerId: selectedFilters?.dealer }),
        fiscalYear: fiscalYear?.value,
        receiptVehicleFromDate: receiptVehicleDate?.from,
        receiptVehicleToDate: receiptVehicleDate?.to,
        locationId: selectedFilters?.location,
        channelId: selectedFilters?.channel,
        ...(processDate?.from && processDate?.to
          ? {
              processFromDate: processDate?.from || null,
              processToDate: processDate?.to || null,
            }
          : {}),
      }
      const data = apiParams({ params, pageNo })
      if (selectedFilters?.dealer) {
        const response = await getSalesReportListApi({ params: data })
        if (response?.data?.list) {
          setSalesReportList(response?.data)
        }
      }
    },
    [selectedFilters, fiscalYear, receiptVehicleDate, processDate],
  )

  const handleScroll = debounce(async e => {
    const { target } = e
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight) {
      if (dealerList.pageNo < dealerList.lastPage) {
        const nextPage = dealerList.pageNo + 1
        // searchDealer
        //   ? await searchDealerAPI({ pageNo: nextPage })
        //   :
        await getDealerListAPI({ pageNo: nextPage })
      }
    }
  }, 300)

  // const dealerListData = searchDealer
  //   ? dealerList?.searchDealerList
  //   : dealerList?.list

  const selectDealerProps = {
    value: selectedFilters?.dealer,
    options: dealerList?.list?.map(({ label, value }) => ({
      label: t(label),
      value,
    })),
    onChange: onSelectDealerChange,
    onPopupScroll: handleScroll,
    loading,
    listHeight: 140,
    // showSearch: true,
    // onSearch: values => onDealerSearch({ values, pageNo: 1 }),
  }

  const selectLocationProps = {
    value: selectedFilters?.location,
    options: dealerList?.selectedDealer?.userLocationChannelDtos?.map(
      ({ locationId, locationName }) => ({
        label: locationName,
        value: locationId,
      }),
    ),
    mode: 'multiple',
    onChange: onSelectLocationChange,
  }

  const channelOptions = Array.from(
    new Map(
      (dealerList?.selectedLocations || []).flatMap(({ channels = [] }) =>
        channels.map(({ channelId, channelKey }) => [
          channelId,
          {
            label: t(channelKey),
            value: channelId,
          },
        ]),
      ),
    ).values(),
  )

  const selectChannelProps = {
    value: selectedFilters?.channel,
    options: channelOptions,
    mode: 'multiple',
    onChange: onSelectChannelChange,
  }

  const onReceiptVehicleDateChange = ([start, end]) => {
    const from = start?.format(SAVE_FORMAT)
    const to = end?.format(SAVE_FORMAT)
    setReceiptVehicleDate({ from, to })
  }

  const onProcessDateChange = dateRange => {
    const from = dateRange?.[0]?.format(SAVE_FORMAT) || ''
    const to = dateRange?.[1]?.format(SAVE_FORMAT) || ''
    setProcessDate({ from, to })
  }

  const handleTableChange = pagination => {
    getStockReportList({ pageNo: pagination?.current })
  }

  const onGenerateReport = async () => {
    const payload = {
      type: 'SALES_REPORT_SHEET',
      ...(selectedFilters?.dealer === 'ALL'
        ? { allDealers: true }
        : { dealerId: selectedFilters?.dealer }),
      fiscalYear: fiscalYear?.value,
      receiptVehicleFromDate: receiptVehicleDate?.from,
      receiptVehicleToDate: receiptVehicleDate?.to,
      locationId: selectedFilters?.location,
      channelId: selectedFilters?.channel,
      ...(processDate?.from && processDate?.to
        ? {
            processFromDate: processDate?.from || null,
            processToDate: processDate?.to || null,
          }
        : {}),
    }

    const response = await exportToExcelApi({ payload })
    if (response?.data?.success) {
      dispatch(
        setPopupMessageModel({
          open: true,
          message: response?.data?.message,
          success: true,
        }),
      )
    }
  }

  return {
    dealerList,
    selectDealerProps,
    dateRangeProps,
    fiscalYearSelector,
    processDate,
    receiptVehicleDate,
    salesReportList,
    onProcessDateChange,
    onReceiptVehicleDateChange,
    handleTableChange,
    onGenerateReport,
    selectLocationProps,
    selectChannelProps,
  }
}

export default salesReport
